import { FormField as FormFieldRoot } from './form-field-root';
import {
  FormFieldLabel,
  FormFieldDescription,
  FormFieldMessage,
} from './form-field-parts';
import {
  FormFieldInput,
  FormFieldGroupInput,
  FormFieldTextArea,
  FormFieldSelect,
  FormFieldMultiSelect,
  FormFieldNumber,
  FormFieldRadioGroup,
  FormFieldCheckbox,
  FormFieldSwitch,
} from './form-field-controls';
import { FormFieldUpload } from './form-field-upload';
import { FormFieldImageGrid } from './form-field-image-grid';
import { FormFieldDatePicker } from './form-field-date-picker';
import { FormFieldTimePicker } from './form-field-time-picker';
import { FormFieldMasked, FormFieldPhone, FormFieldPrice } from './form-field-formatted';

/**
 * `FormField` — a generic, RHF-driven compound form control.
 *
 * The root wires the field via an RHF `Controller` and shares it through
 * context, so subcomponents auto-bind:
 *
 * ```tsx
 * <FormField name="email">
 *   <FormField.Label>Email</FormField.Label>
 *   <FormField.Input type="email" placeholder="you@example.com" />
 *   <FormField.Description>We never share it.</FormField.Description>
 *   <FormField.Message />
 * </FormField>
 * ```
 *
 * RHF owns form state; these components are presentational only.
 */
export const FormField = Object.assign(FormFieldRoot, {
  Label: FormFieldLabel,
  Description: FormFieldDescription,
  Message: FormFieldMessage,
  Input: FormFieldInput,
  GroupInput: FormFieldGroupInput,
  Number: FormFieldNumber,
  TextArea: FormFieldTextArea,
  Select: FormFieldSelect,
  MultiSelect: FormFieldMultiSelect,
  RadioGroup: FormFieldRadioGroup,
  Checkbox: FormFieldCheckbox,
  Switch: FormFieldSwitch,
  Upload: FormFieldUpload,
  ImageGrid: FormFieldImageGrid,
  DatePicker: FormFieldDatePicker,
  TimePicker: FormFieldTimePicker,
  Phone: FormFieldPhone,
  Price: FormFieldPrice,
  Masked: FormFieldMasked,
});

export { FORM_FIELD_MESSAGE, FORM_FIELD_MESSAGE_INFLOW } from './form-field-parts';
export type { FormFieldProps } from './form-field-root';
export type { FieldOption } from './form-field-controls';
export type { MultiSelectOption } from '../multi-select';
export type { ProductImage } from './form-field-image-grid';
export { emptyPrice } from './form-field-formatted';
export { deriveFieldAutoComplete } from './derive-auto-complete';
export type {
  FormFieldPhoneProps,
  FormFieldPriceProps,
  FormFieldMaskedProps,
} from './form-field-formatted';
